import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  useFloating,
  useHover,
  useRole,
  useInteractions,
  FloatingPortal,
  autoUpdate,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import { createTooltipMiddleware } from '../../utils/floatingUiUtils';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: Placement;
  onShow?: () => void;
  onHide?: () => void;
  controlled?: boolean;
}

export interface TooltipRef {
  show: () => void;
  hide: () => void;
}

const ARROW_SIZE = 10; // rotated square dimensions

const Tooltip = forwardRef<TooltipRef, TooltipProps>(
  (
    { content, placement = 'bottom', children, onShow, onHide, controlled = false },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    const arrowRef = useRef<HTMLDivElement>(null);

    // Use consolidated middleware configuration
    const middleware = useMemo(() => createTooltipMiddleware(arrowRef), []);

    const { refs, floatingStyles, context, middlewareData } = useFloating({
      open: isVisible,
      onOpenChange: setIsVisible,
      placement,
      middleware,
      whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, {
      enabled: !controlled,
      delay: { open: 0, close: 100 },
    });
    const role = useRole(context, { role: 'tooltip' });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, role]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        // No timeout cleanup needed with Floating UI
      };
    }, []);

    const showTooltip = useCallback(() => {
      setIsVisible(true);
      onShow?.();
    }, [onShow]);

    const hideTooltip = useCallback(() => {
      setIsVisible(false);
      onHide?.();
    }, [onHide]);

    // Expose methods for controlled usage
    useImperativeHandle(
      ref,
      () => ({
        show: showTooltip,
        hide: hideTooltip,
      }),
      [showTooltip, hideTooltip],
    );

    function getArrowPosition(placement?: string): React.CSSProperties {
      const basePlacement = (placement || 'bottom').split('-')[0];
      const offset = -(ARROW_SIZE / 2) + 2; // overlap into the tooltip body

      switch (basePlacement) {
        case 'top':
          return { bottom: `${offset}px` };
        case 'bottom':
          return { top: `${offset}px` };
        case 'left':
          return { right: `${offset}px` };
        case 'right':
          return { left: `${offset}px` };
        default:
          return {};
      }
    }

    function getEntryTransform(placement?: string): string {
      const basePlacement = (placement || 'bottom').split('-')[0];
      // Slide in from the opposite direction the arrow points
      switch (basePlacement) {
        case 'top':
          return 'translateY(4px) scale(0.97)';
        case 'bottom':
          return 'translateY(-4px) scale(0.97)';
        case 'left':
          return 'translateX(4px) scale(0.97)';
        case 'right':
          return 'translateX(-4px) scale(0.97)';
        default:
          return 'scale(0.97)';
      }
    }

    return (
      <>
        <span
          ref={refs.setReference}
          className="tooltip-trigger"
          {...getReferenceProps()}
        >
          {children}
        </span>
        {isVisible && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className="tooltip"
              {...getFloatingProps()}
            >
              <div
                style={
                  {
                    '--tooltip-entry': getEntryTransform(context.placement),
                  } as React.CSSProperties
                }
                className="tooltip-body"
              >
                {content}
                {middlewareData.arrow && (
                  <div
                    ref={arrowRef}
                    className="tooltip-arrow"
                    style={{
                      left:
                        middlewareData.arrow.x != null
                          ? `${middlewareData.arrow.x}px`
                          : '',
                      top:
                        middlewareData.arrow.y != null
                          ? `${middlewareData.arrow.y}px`
                          : '',
                      ...getArrowPosition(context.placement),
                    }}
                  />
                )}
              </div>
            </div>
          </FloatingPortal>
        )}
      </>
    );
  },
);

export default Tooltip;
